import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackSegment } from '~/components/SegmentAnalytics';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { Filters } from '~/features/advanced-search/filters/Filters';
import { attachRenderersAndSerializers } from '~/features/advanced-search/filters/Filters.utils';
import { QueryBuilder } from '~/features/advanced-search/query-builder/QueryBuilder';
import { ReadOnlyComplexQuery } from '~/features/advanced-search/read-only-complex-query/ReadOnlyComplexQuery';
import { SearchQuery } from '~/features/advanced-search/types';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { DashboardEntityType } from '~/redux/api/methods/dashboardV2';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';

type Props = {
  entityType?: DashboardEntityType;
};

export const DashboardV3Filters = ({ entityType }: Props) => {
  const [isQueryBuilderVisible, _toggle, showQueryBuilder, hideQueryBuilder] =
    useToggle(false);

  const { data: searchFiltersData } = api.endpoints.getFiltersV2.useQuery(
    { type: entityType! },
    { skip: !entityType },
  );
  const searchFilters = searchFiltersData ?? {};

  const dispatch = useDispatch();
  const selectedFilters = useSelector(
    slices.dashboardV2.selectors.selectSelectedFilters,
  );
  const crossFilters = useSelector(
    slices.dashboardV2.selectors.selectCrossFilters,
  );
  const queryBuilder = useSelector(
    slices.dashboardV2.selectors.selectQueryBuilder,
  );
  const user = useCurrentUser();

  const fieldsWithLazyLoad = useMemo(
    () =>
      attachRenderersAndSerializers({
        filters: Object.values(searchFilters),
        type: entityType,
      }),
    [searchFilters],
  );

  const onFiltersReset = () => {
    trackSegment('resetFilters', {
      user_id: user.id,
      client_id: user.client,
    });
    dispatch(slices.dashboardV2.actions.resetFilters({ searchFilters }));
    dispatch(slices.dashboardV2.actions.setCrossFilters([]));
  };

  const onQueryBuilderChange = (query: Nullable<SearchQuery>) => {
    dispatch(slices.dashboardV2.actions.setQueryBuilder(query));
    dispatch(slices.dashboardV2.actions.setPage(1));
    dispatch(slices.dashboardV2.actions.setDashboardDirty(true));
    hideQueryBuilder();
  };

  return (
    <>
      {queryBuilder ? (
        <ReadOnlyComplexQuery
          queryBuilder={queryBuilder}
          searchFilters={searchFilters || {}}
          onClear={() => onQueryBuilderChange(null)}
          onEdit={showQueryBuilder}
        />
      ) : (
        <Filters
          crossFilters={crossFilters}
          searchFilters={fieldsWithLazyLoad}
          selectedFilters={selectedFilters}
          onChange={(filters) => {
            dispatch(slices.dashboardV2.actions.setSelectedFilters(filters));
            dispatch(slices.dashboardV2.actions.setPage(1));
            dispatch(slices.dashboardV2.actions.setDashboardDirty(true));
          }}
          onFiltersReset={onFiltersReset}
          onChangeCrossFilters={(newFilters) => {
            dispatch(slices.dashboardV2.actions.setCrossFilters(newFilters));
          }}
          onQueryButtonClick={showQueryBuilder}
        />
      )}
      {isQueryBuilderVisible && (
        <QueryBuilder
          query={queryBuilder}
          searchFilters={fieldsWithLazyLoad}
          onHide={hideQueryBuilder}
          onQueryChange={onQueryBuilderChange}
        />
      )}
    </>
  );
};
